import { useQuery } from '@tanstack/react-query';
import { ERC20_ABI } from '../config/oft';
import { DEPOSIT_CHAIN_BY_ID } from '../config/chains';
import { getSourceChainPublicClient } from '../utils/getSourceChainPublicClient';

export interface OftAllowanceData {
  /** Raw allowance as BigInt */
  allowance: bigint;
  /** Whether the current allowance is sufficient for the given amount */
  isApproved: (amount: bigint) => boolean;
}

/**
 * Checks the token allowance for the OFT contract on the source chain.
 *
 * - On Ethereum: checks USDT allowance for the OFT Adapter
 * - On Arbitrum: checks USDT0 allowance for the OFT contract
 *
 * Uses a chain-specific publicClient (NOT the wagmi-connected wallet chain).
 *
 * @param sourceChainId - EVM chain ID of the source chain
 * @param address - Owner address to check allowance for
 */
export function useOftAllowance(
  sourceChainId: number | undefined,
  address: `0x${string}` | undefined,
) {
  return useQuery<OftAllowanceData>({
    queryKey: ['oftAllowance', sourceChainId, address],
    queryFn: async () => {
      if (!sourceChainId || !address) {
        throw new Error('sourceChainId and address are required');
      }

      const chainConfig = DEPOSIT_CHAIN_BY_ID[sourceChainId];
      if (!chainConfig) {
        throw new Error(`No chain config for chainId ${sourceChainId}`);
      }

      const publicClient = getSourceChainPublicClient(sourceChainId);

      const allowance = await publicClient.readContract({
        address: chainConfig.tokenAddress,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [address, chainConfig.oftAddress],
      });

      return {
        allowance,
        isApproved: (amount: bigint) => allowance >= amount,
      };
    },
    enabled: !!sourceChainId && !!address,
    staleTime: 15_000,
  });
}
